import { useEffect, useState } from "react";

import { chatApiInstance, type ThreadResolverState } from "../apis/chatApi";

const ACTIVE_STATES = new Set(["running", "waiting"]);
export const TICKET_RESOLVER_STATE_CHANGED_EVENT = "theo:ticket-resolver-state-changed";

export function announceTicketResolverState(
  aiThreadId: string | number,
  state: ThreadResolverState
): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(TICKET_RESOLVER_STATE_CHANGED_EVENT, {
      detail: { aiThreadId: String(aiThreadId), state: normalizeState(state) },
    })
  );
}

function normalizeState(response: ThreadResolverState): ThreadResolverState {
  let context = response?.resolver_context;
  if (typeof context === "string") {
    try {
      context = JSON.parse(context);
    } catch {
      context = null;
    }
  }
  return { ...response, resolver_context: context };
}

/**
 * Reads the ticket's durable Auto Resolver state and refreshes it while execution is active.
 * A missing run is normal for plain chat and is intentionally represented by null.
 */
export default function useTicketResolverState(
  aiThreadId: string | number | null | undefined,
  ticketId: string | null | undefined
): ThreadResolverState | null {
  const threadKey = aiThreadId != null && aiThreadId !== "" ? String(aiThreadId) : null;
  const [state, setState] = useState<ThreadResolverState | null>(null);

  useEffect(() => {
    if (!threadKey || !ticketId) {
      setState(null);
      return undefined;
    }
    const activeThreadKey = threadKey;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let liveRevision = 0;
    const apply = (next: ThreadResolverState) => {
      if (cancelled) return;
      if (timer) clearTimeout(timer);
      setState(next);
      if (ACTIVE_STATES.has(next.resolver_state || "")) {
        timer = setTimeout(read, 5000);
      }
    };
    async function read() {
      const revisionAtStart = liveRevision;
      try {
        const next = normalizeState(await chatApiInstance.getThreadResolverState(activeThreadKey));
        if (revisionAtStart !== liveRevision) return;
        apply(next);
      } catch {
        if (!cancelled && revisionAtStart === liveRevision) setState(null);
      }
    }
    const onStateChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ aiThreadId?: string; state?: ThreadResolverState }>).detail;
      if (String(detail?.aiThreadId || "") !== activeThreadKey || !detail?.state) return;
      liveRevision += 1;
      apply(normalizeState(detail.state));
    };

    setState(null);
    window.addEventListener(TICKET_RESOLVER_STATE_CHANGED_EVENT, onStateChanged);
    read();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener(TICKET_RESOLVER_STATE_CHANGED_EVENT, onStateChanged);
    };
  }, [threadKey, ticketId]);

  return state;
}

